import type { ProjectConfig, Scene } from "../models/production";

export interface ScriptSceneInput {
  sceneNumber: string | number;
  heading?: string;
  locationName?: string;
  location?: string;
  intExt?: string;
  timeOfDay?: string;
  dayNight?: string;
  description?: string;
  scriptText?: string;
  characters?: string[];
  aiSummary?: string;
  inferredVfxRequirements?: string[];
  estimatedComplexity?: string;
  scriptPageRange?: string;
  sequenceNumber?: number | string;
}

function normalizeWhitespace(value?: string | null) {
  return value?.replace(/\s+/g, " ").trim() || undefined;
}

function normalizeScriptBlock(value?: string | null) {
  if (!value) return undefined;
  return value
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim() || undefined;
}

export function normalizeSceneNumber(value: string | number | undefined | null) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

export function extractPotentialVfxRequirements(sceneText?: string, aiMetadata?: Partial<ScriptSceneInput>) {
  const combined = [sceneText, aiMetadata?.description, aiMetadata?.aiSummary].filter(Boolean).join(" ").toLowerCase();
  const seeded = new Set((aiMetadata?.inferredVfxRequirements || []).map((item) => item.trim()).filter(Boolean));
  const keywords = [
    "screen replacement",
    "set extension",
    "cleanup",
    "cg environment",
    "matte painting",
    "wire removal",
    "crowd",
    "blood",
    "explosion",
    "muzzle flash",
  ];

  for (const keyword of keywords) {
    if (combined.includes(keyword)) {
      seeded.add(keyword);
    }
  }

  return Array.from(seeded);
}

export function normalizeScriptSceneInput(input: ScriptSceneInput) {
  return {
    sceneNumber: normalizeSceneNumber(input.sceneNumber),
    heading: normalizeWhitespace(input.heading),
    locationName: normalizeWhitespace(input.locationName || input.location),
    intExt: normalizeWhitespace(input.intExt),
    dayNight: normalizeWhitespace(input.dayNight || input.timeOfDay),
    description: normalizeWhitespace(input.description),
    scriptText: normalizeScriptBlock(input.scriptText),
    characters: (input.characters || []).map((item) => item.trim()).filter(Boolean),
    aiSummary: normalizeWhitespace(input.aiSummary),
    inferredVfxRequirements: extractPotentialVfxRequirements(input.scriptText, input),
    estimatedComplexity: normalizeWhitespace(input.estimatedComplexity),
    scriptPageRange: normalizeWhitespace(input.scriptPageRange),
    sequenceNumber:
      typeof input.sequenceNumber === "number"
        ? input.sequenceNumber
        : input.sequenceNumber
          ? Number.parseInt(String(input.sequenceNumber), 10)
          : undefined,
  };
}

export function buildSceneId(episodeId: string, sceneNumber: string) {
  return `${episodeId}:scene:${sceneNumber}`;
}

export function inferSequenceNumberFromScene(
  sceneNumber: string,
  projectConfig?: Partial<ProjectConfig> | null,
  explicitSequenceNumber?: number
) {
  if (typeof explicitSequenceNumber === "number" && !Number.isNaN(explicitSequenceNumber)) {
    return explicitSequenceNumber;
  }

  const numericScene = Number.parseInt(sceneNumber.replace(/[^\d]/g, ""), 10);
  if (Number.isNaN(numericScene)) return undefined;

  const explicitRule = projectConfig?.sceneSequenceMappingRules?.sceneNumberDivisor;
  if (typeof explicitRule === "number" && explicitRule > 0) {
    return Math.max(1, Math.ceil(numericScene / explicitRule));
  }

  return numericScene;
}

export function mapScriptSceneToSceneRecord(args: {
  projectId: string;
  seasonId?: string;
  episodeId: string;
  existingScene?: Partial<Scene>;
  input: ScriptSceneInput;
  projectConfig?: Partial<ProjectConfig> | null;
}): Scene {
  const normalized = normalizeScriptSceneInput(args.input);
  const now = new Date().toISOString();

  return {
    id: args.existingScene?.id || buildSceneId(args.episodeId, normalized.sceneNumber),
    projectId: args.projectId,
    seasonId: args.seasonId,
    episodeId: args.episodeId,
    sequenceId: args.existingScene?.sequenceId,
    sceneNumber: normalized.sceneNumber,
    sequenceNumber: inferSequenceNumberFromScene(normalized.sceneNumber, args.projectConfig, normalized.sequenceNumber),
    scriptPageRange: normalized.scriptPageRange,
    heading: normalized.heading,
    locationName: normalized.locationName,
    intExt: normalized.intExt,
    dayNight: normalized.dayNight,
    characters: normalized.characters,
    description: normalized.description,
    scriptText: normalized.scriptText,
    aiSummary: normalized.aiSummary,
    inferredVfxRequirements: normalized.inferredVfxRequirements,
    estimatedComplexity: normalized.estimatedComplexity,
    relatedShotIds: args.existingScene?.relatedShotIds || [],
    relatedAssetIds: args.existingScene?.relatedAssetIds || [],
    createdAt: args.existingScene?.createdAt || now,
    updatedAt: now,
  };
}
